import { Toaster } from "@repo/ui/toaster";

import { TooltipProvider } from "@repo/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import Auth from "./pages/Auth";
import SetPassword from "./pages/SetPassword";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Jobs from "./pages/Jobs";
import JobRunDetail from "./pages/JobRunDetail";
import Suburbs from "./pages/Suburbs";
import SuburbDetail from "./pages/SuburbDetail";
import Adapters from "./pages/Adapters";
import AdapterDetail from "./pages/AdapterDetail";
import SuburbAdapters from "./pages/SuburbAdapters";
import SuburbAdapterDetail from "./pages/SuburbAdapterDetail";
import SuburbInformation from "./pages/SuburbInformation";
import Exports from "./pages/Exports";
import Analysis from "./pages/Analysis";
import AI from "./pages/AI";
import Valuations from "./pages/Valuations";
import DataConnections from "./pages/DataConnections";
import DataIngress from "./pages/DataIngress";
import PoisonedData from "./pages/PoisonedData";
import Settings from "./pages/Settings";
import Queue from "./pages/Queue";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Agencies from "./pages/Agencies";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const RedirectToSuburb = () => {
  const { suburbId } = useParams<{ suburbId: string }>();
  return <Navigate to={`/dashboard/suburbs/${suburbId ?? ""}`} replace />;
};

if (import.meta.env.DEV) {
  console.log(`${import.meta.env.MODE} environment variables:`);
  console.log(import.meta.env);
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />

        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/set-password" element={<SetPassword />} />
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="jobs/:jobId" element={<Jobs />} />
              <Route path="jobs/:jobId/runs/:runId" element={<JobRunDetail />} />
              <Route path="suburbs" element={<Suburbs />} />
              <Route path="suburbs/:suburbId" element={<SuburbDetail />} />
              <Route path="adapters" element={<Adapters />} />
              <Route path="adapters/:slug" element={<AdapterDetail />} />
              <Route path="suburb-adapters" element={<SuburbAdapters />} />
              <Route path="suburb-adapters/:slug" element={<SuburbAdapterDetail />} />
              <Route path="suburb-information" element={<SuburbInformation />} />
              <Route
                path="suburb-information/:suburbId"
                element={<RedirectToSuburb />}
              />
              <Route path="queue" element={<Queue />} />
              <Route path="properties" element={<Properties />} />
              <Route path="properties/:propertyId" element={<PropertyDetail />} />
              <Route path="agencies" element={<Agencies />} />
              <Route path="exports" element={<Exports />} />
              <Route path="storage" element={<Navigate to="/dashboard/exports" replace />} />
              <Route path="analysis" element={<Analysis />} />
              <Route path="ai" element={<AI />} />
              <Route path="poisoned-data" element={<PoisonedData />} />
              <Route path="data-connections" element={<DataConnections />} />
              <Route path="data-ingress" element={<DataIngress />} />
              <Route path="valuations" element={<Valuations />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
